import { useState, useEffect } from 'react';
import apiService from '../services/api';
import './UploadPage.css';
import { useSession } from '../contexts/SessionContext';
import { useOffline } from '../contexts/OfflineContext';
import offlineStorage from '../services/offlineStorage';
import Toast from '../components/Toast';

const UploadPage = () => {
  const session = useSession();
  const { isOnline, pendingUploadCount, isSyncing, refreshPendingCount, triggerSync } = useOffline();
  const [content, setContent] = useState('');
  const [source, setSource] = useState('diary');
  const [sources, setSources] = useState<string[]>(['diary']);
  const [newSource, setNewSource] = useState('');
  const [showNewSourceModal, setShowNewSourceModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [delimiter, setDelimiter] = useState('');
  const [loading, setLoading] = useState(false);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  const [autoCategorize, setAutoCategorize] = useState(true);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error' | 'info';
    duration?: number;
  } | null>(null);
  const [uploadStats, setUploadStats] = useState<{
    chunksProcessed: number;
    userChunkCount?: number;
    maxChunksPerUser?: number;
  } | null>(null);

  // Load user sources on mount
  useEffect(() => {
    const loadSources = async () => {
      try {
        const userId = session.userId;
        const response = await apiService.getUserSources(userId);
        const loadedSources = response.data.sources;
        setSources(loadedSources);
        // Update source state to first available source if current source is not in the list
        setSource(prevSource => {
          if (loadedSources.length > 0 && !loadedSources.includes(prevSource)) {
            return loadedSources[0];
          }
          return prevSource;
        });
      } catch (error) {
        console.error('Error loading sources:', error);
        // Keep default sources if loading fails
      } finally {
        setSourcesLoading(false);
      }
    };
    loadSources();
  }, [session.userId]);

  const handleAddSource = async () => {
    if (!newSource.trim()) {
      setToast({ message: 'Please enter a source name', type: 'error' });
      return;
    }
    if (sources.includes(newSource.trim())) {
      setToast({ message: 'Source already exists', type: 'error' });
      return;
    }

    try {
      const userId = session.userId;
      const addedSource = newSource.trim();
      const response = await apiService.addUserCategory(userId, addedSource);
      setSources(response.data.sources);
      setSource(addedSource); // Switch to the newly created source
      setNewSource('');
      setShowNewSourceModal(false);
      setToast({ message: 'Category added successfully', type: 'success', duration: 3000 });
    } catch (error: any) {
      const errorDetail = error.response?.data?.detail || error.message;
      setToast({ message: `Error: ${errorDetail}`, type: 'error' });
    }
  };

  const handleDeleteSource = async () => {
    if (sources.length <= 1) {
      setToast({ message: 'Cannot delete the last remaining category', type: 'error' });
      return;
    }

    try {
      const userId = session.userId;
      const response = await apiService.deleteUserCategory(userId, source);
      setSources(response.data.sources);
      // Switch to the first available source after deletion
      setSource(response.data.sources[0]);
      setShowDeleteConfirm(false);
      setToast({ message: 'Category deleted successfully', type: 'success', duration: 3000 });
    } catch (error: any) {
      const errorDetail = error.response?.data?.detail || error.message;
      setToast({ message: `Error: ${errorDetail}`, type: 'error' });
    }
  };

  const canDeleteCurrentSource = sources.length > 1;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim()) {
      setToast({ message: 'Please enter some content to upload', type: 'error' });
      return;
    }

    setLoading(true);
    setToast(null);
    setUploadStats(null);

    try {
      // Get userId from SessionContext (no localStorage)
      const userId = session.userId;

      // Check if we're offline
      if (!isOnline) {
        // Save to IndexedDB for later sync
        await offlineStorage.addPendingUpload({
          content,
          delimiter,
          userId,
          source,
          autoCategorize,
        });

        setToast({
          message: 'You are offline. Upload saved and will sync when back online.',
          type: 'info',
          duration: 5000
        });
        setContent('');
        await refreshPendingCount();
      } else {
        // Online: upload directly
        const response = await apiService.uploadDiary(content, delimiter, userId, source, autoCategorize);

        setToast({
          message: `Success: ${response.data.message}. Processed ${response.data.chunks_processed} chunks.`,
          type: 'success'
        });
        setUploadStats({
          chunksProcessed: response.data.chunks_processed,
          ...(response.data.user_chunk_count !== undefined && { userChunkCount: response.data.user_chunk_count }),
          ...(response.data.max_chunks_per_user !== undefined && { maxChunksPerUser: response.data.max_chunks_per_user }),
        });
        setContent('');
      }
    } catch (error: any) {
      const errorDetail = error.response?.data?.detail || error.message;
      setToast({ message: `Error: ${errorDetail}`, type: 'error' });
      setUploadStats(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="upload-page">
      <form onSubmit={handleSubmit} className="upload-form">
        <div className="form-group">
          <div className="category-header">
            <label htmlFor="source">Category:</label>
            <label className="auto-toggle">
              <input
                type="checkbox"
                checked={autoCategorize}
                onChange={(e) => setAutoCategorize(e.target.checked)}
                disabled={sourcesLoading}
              />
              <span className="toggle-label">Auto</span>
            </label>
          </div>
          <div className="source-controls">
            <select
              id="source"
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className={`source-dropdown ${autoCategorize ? 'disabled-by-auto' : ''}`}
              disabled={sourcesLoading || autoCategorize}
            >
              {sourcesLoading ? (
                <option value="diary" disabled>Loading...</option>
              ) : (
                sources.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))
              )}
            </select>
            <button
              type="button"
              onClick={() => setShowNewSourceModal(true)}
              className="new-source-btn"
              title="Add new source"
              disabled={sourcesLoading}
            >
              +
            </button>
            {canDeleteCurrentSource && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="delete-source-btn"
                title="Delete current source"
                disabled={sourcesLoading}
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="delimiter">Delimiter:</label>
          <input
            id="delimiter"
            type="text"
            value={delimiter}
            onChange={(e) => setDelimiter(e.target.value)}
            placeholder="Enter delimiter (e.g., ##)"
            className="delimiter-input"
          />
          <small className="help-text">Default: ##. If no delimiter is found, splits by every 10000 characters.</small>
        </div>

        <div className="form-group">
          <label htmlFor="content">Content:</label>
          <textarea
            id="content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Paste your corpus content here..."
            className="content-textarea"
            rows={15}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="submit-button"
        >
          {loading ? 'Uploading...' : 'Upload Content'}
        </button>
      </form>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast(null);
            setUploadStats(null);
          }}
          duration={toast.duration}
        >
          {uploadStats && uploadStats.userChunkCount !== undefined && uploadStats.maxChunksPerUser !== undefined && (
            <div className="toast-stats">
              <h3>Upload Statistics</h3>
              <div className="toast-stats-grid">
                <div className="toast-stat-item">
                  <span className="toast-stat-label">Chunks Processed:</span>
                  <span className="toast-stat-value">{uploadStats.chunksProcessed}</span>
                </div>
                <div className="toast-stat-item">
                  <span className="toast-stat-label">Your Total Chunks:</span>
                  <span className="toast-stat-value">{uploadStats.userChunkCount}</span>
                </div>
                <div className="toast-stat-item">
                  <span className="toast-stat-label">Maximum Allowed:</span>
                  <span className="toast-stat-value">{uploadStats.maxChunksPerUser}</span>
                </div>
                <div className="toast-stat-item">
                  <span className="toast-stat-label">Remaining Capacity:</span>
                  <span className="toast-stat-value">
                    {uploadStats.maxChunksPerUser - uploadStats.userChunkCount} chunks
                  </span>
                </div>
              </div>
              <div className="toast-usage-bar">
                <div
                  className="toast-usage-bar-fill"
                  style={{
                    width: `${(uploadStats.userChunkCount / uploadStats.maxChunksPerUser) * 100}%`,
                    backgroundColor: uploadStats.userChunkCount / uploadStats.maxChunksPerUser > 0.9 ? '#ff4444' : 'rgba(255, 255, 255, 0.9)'
                  }}
                ></div>
              </div>
              <div className="toast-usage-text">
                {Math.round((uploadStats.userChunkCount / uploadStats.maxChunksPerUser) * 100)}% of capacity used
              </div>
            </div>
          )}
        </Toast>
      )}

      {/* Offline status indicator - moved to bottom */}
      {!isOnline && (
        <div className="offline-banner">
          <span className="offline-icon">📡</span> You are offline. Uploads will be saved and synced when back online.
        </div>
      )}

      {/* Pending uploads indicator - moved to bottom */}
      {pendingUploadCount > 0 && (
        <div className="pending-uploads-banner">
          <span className="pending-icon">⏳</span>
          {pendingUploadCount} upload{pendingUploadCount > 1 ? 's' : ''} pending sync
          {isOnline && !isSyncing && (
            <button
              className="sync-now-btn"
              onClick={triggerSync}
              title="Sync now"
            >
              Sync Now
            </button>
          )}
          {isSyncing && <span className="syncing-text"> (Syncing...)</span>}
        </div>
      )}

      {/* Modals */}
      {showNewSourceModal && (
        <div className="modal-overlay" onClick={() => setShowNewSourceModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add New Category</h3>
            <input
              type="text"
              value={newSource}
              onChange={(e) => setNewSource(e.target.value)}
              placeholder="Category name"
              className="source-name-input"
              onKeyPress={(e) => e.key === 'Enter' && handleAddSource()}
              autoFocus
            />
            <div className="modal-buttons">
              <button onClick={handleAddSource} disabled={!newSource.trim()}>
                Add
              </button>
              <button onClick={() => setShowNewSourceModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Category</h3>
            <p>Are you sure you want to delete "{source}"?</p>
            <p>This action cannot be undone.</p>
            <div className="modal-buttons">
              <button onClick={handleDeleteSource} className="delete-btn">
                Delete
              </button>
              <button onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadPage;
